import { Box, Button, FormField, Input } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useRef, useState } from 'react';

import { EMBED_TYPE } from './../../ricos-content';
import {
  EditorContext,
  ModalServiceContext,
  PluginsEventsContext,
  RicosContext,
} from './../../ricos-context';
import type { EmbedData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { containsUrl } from './../../_shared/link/contains-url';
import { MEDIA_POPOVERS_BUTTONS_NAMES_BI } from './../../wix-rich-content-common';
import { Panel, PanelHeader } from './../../ui-components';

import { socialModals } from '../consts';
import {
  SOCIAL_EMBED_MODAL,
  SOCIAL_EMBED_MODAL_INPUT,
  SOCIAL_EMBED_MODAL_SUBMIT_BTN,
} from '../data-hooks';
import { EMBED_DEFAULTS } from '../defaults';
import classes from '../statics/styles/insert-modal-scss';
import type { LinkPreviewPluginEditorConfig } from '../types';
import { LINK_PREVIEW_TYPE } from '../types';

export type InsertModalProps = {
  fetchData: LinkPreviewPluginEditorConfig['fetchData'];
  socialType: 'Instagram' | 'Twitter' | 'Pinterest' | 'Facebook' | 'TikTok';
  componentData?: EmbedData;
  nodeId?: string;
};

export const InsertModal: FC<InsertModalProps> = ({
  componentData,
  fetchData,
  nodeId,
  socialType,
}) => {
  const { t } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);

  const logBi = useBiNext({
    pluginId: 'linkPreview',
    modalId: socialModals[socialType.toLowerCase() as $TSFixMeKey<typeof socialModals>],
    nodeId,
  });
  const [url, setUrl] = useState('');
  const [submittedInvalidUrl, setSubmittedInvalidUrl] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pluginEvents = {
    onPluginsPopOverClick: (data: $TSFixMe) => pluginsEvents.publishPluginPopoverClick(data),
  };

  const closeModal = () => {
    setSubmittedInvalidUrl(false);
    modalService.closeModal(
      socialModals[socialType.toLowerCase() as $TSFixMeKey<typeof socialModals>]
    );
  };

  const onConfirm = (embedData: EmbedData) => {
    const publicCommands = getPublicCommands();
    if (nodeId) {
      publicCommands?.setBlock(nodeId, EMBED_TYPE, embedData);
    } else {
      publicCommands?.insertBlockWithBlankLines(EMBED_TYPE, embedData);
    }
  };

  const handleOnConfirm = async () => {
    pluginEvents?.onPluginsPopOverClick?.({
      pluginId: LINK_PREVIEW_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.embed,
    });

    logBi({ dataHook: SOCIAL_EMBED_MODAL_SUBMIT_BTN });

    if (url) {
      try {
        const { html } = (await fetchData?.(url)) || {};
        if (!html) {
          setSubmittedInvalidUrl(true);
          inputRef.current?.focus();
          return;
        }
        onConfirm({
          ...componentData,
          ...EMBED_DEFAULTS,
          src: url,
          oembed: {
            type: 'rich',
            html,
          },
        });

        closeModal();
      } catch {
        setSubmittedInvalidUrl(true);
        inputRef.current?.focus();
      }
    } else {
      setSubmittedInvalidUrl(true);
      inputRef.current?.focus();
    }
  };

  const onCloseRequested = () => {
    pluginEvents?.onPluginsPopOverClick?.({
      pluginId: LINK_PREVIEW_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.cancel,
    });
    closeModal();
  };

  return (
    <Panel onCloseButtonClick={onCloseRequested} dataHook={SOCIAL_EMBED_MODAL} height="100%">
      <PanelHeader title={t(`EmbedURL_Social_${socialType}_Header_Title`)} />

      <Box direction="vertical" gap="SP3" padding="SP3">
        <FormField
          label={t(`EmbedURL_Social_${socialType}_Title`)}
          maxLines={2}
          status={submittedInvalidUrl ? 'error' : undefined}
          statusMessage={
            submittedInvalidUrl ? t('SoundCloudUploadModal_Input_InvalidUrl') : undefined
          }
        >
          <Input
            inputRef={ref => {
              inputRef.current = ref;
            }}
            className={classes.input}
            type="url"
            id="dropdown-text-input"
            autocomplete="off"
            textOverflow="ellipsis"
            value={url}
            dataHook={SOCIAL_EMBED_MODAL_INPUT}
            onChange={e => {
              setSubmittedInvalidUrl(false);
              logBi({ dataHook: SOCIAL_EMBED_MODAL_INPUT, value: e.target.value });
              setUrl(e.target.value);
            }}
            placeholder={t(`EmbedURL_Social_${socialType}_Placeholder`)}
            onBlur={() => {
              if (url && !containsUrl(url)) {
                setSubmittedInvalidUrl(true);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleOnConfirm();
              }
            }}
          />
        </FormField>
        <Button
          dataHook={SOCIAL_EMBED_MODAL_SUBMIT_BTN}
          onClick={() => {
            handleOnConfirm();
          }}
          size="small"
        >
          {t('Embed_Add_Button_Label')}
        </Button>
      </Box>
    </Panel>
  );
};
